import { Check } from "lucide-react";
import { formatRupiah, parseRupiah } from "@/lib/assessmentConfig";

export function QuestionBlock({ label, helper, children, testid }) {
  return (
    <div className="space-y-3" data-testid={testid}>
      <div>
        <h3 className="font-heading text-base sm:text-lg font-semibold text-ink">{label}</h3>
        {helper && <p className="mt-1 text-sm text-ink-muted leading-relaxed">{helper}</p>}
      </div>
      {children}
    </div>
  );
}

// Single-select option cards
export function SingleSelect({ options, value, onChange, name, columns = 1 }) {
  const grid = columns === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1";
  return (
    <div className={`grid ${grid} gap-2.5`}>
      {options.map((opt) => {
        const val = typeof opt === "string" ? opt : opt.value;
        const label = typeof opt === "string" ? opt : opt.label;
        const desc = typeof opt === "string" ? null : opt.desc;
        const active = value === val;
        return (
          <button
            key={val}
            type="button"
            data-testid={`${name}-${val}`}
            onClick={() => onChange(val)}
            className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
              active ? "border-brand-accent bg-brand/5 ring-1 ring-brand-accent" : "border-borderc bg-white hover:border-brand-accent/50"
            }`}
          >
            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
              active ? "border-brand-accent bg-brand-accent text-white" : "border-slate-300"
            }`}>
              {active && <Check className="h-3.5 w-3.5" />}
            </span>
            <span>
              <span className={`block text-sm font-medium ${active ? "text-ink" : "text-ink-soft"}`}>{label}</span>
              {desc && <span className="mt-0.5 block text-xs text-ink-muted leading-relaxed">{desc}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Multi-select chips with optional max
export function MultiSelect({ options, values, onChange, name, max }) {
  const toggle = (val) => {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      if (max && values.length >= max) return;
      onChange([...values, val]);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = values.includes(opt);
        const disabled = max && !active && values.length >= max;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`${name}-${opt}`}
            onClick={() => toggle(opt)}
            disabled={disabled}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              active
                ? "border-brand bg-brand text-white"
                : disabled
                ? "border-borderc bg-slate-50 text-slate-300 cursor-not-allowed"
                : "border-borderc bg-white text-ink-soft hover:border-brand-accent"
            }`}
          >
            {active && <Check className="h-3.5 w-3.5" />}
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function CurrencyField({ value, onChange, name, placeholder }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted font-medium">Rp</span>
      <input
        type="text"
        inputMode="numeric"
        data-testid={name}
        placeholder={placeholder}
        value={value === null || value === "" ? "" : Number(value).toLocaleString("id-ID")}
        onChange={(e) => onChange(parseRupiah(e.target.value))}
        className="h-12 w-full rounded-xl border border-borderc bg-white pl-11 pr-4 text-base font-semibold text-ink outline-none transition-colors focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
      />
    </div>
  );
}

export { formatRupiah };
